import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { BidFormValues } from "../admin/bids/types";
import { useWatch } from "react-hook-form";

/**
 * `showFirmnessLevel` defaults to true, so the admin bid screens that share this
 * component are unaffected. The public bid request form passes false: firmness
 * is an operational call an admin makes when pricing the bid, not something a
 * customer is well placed to judge, so it is asked at finalization instead.
 */
export const AdditionalDetailsFields = ({
  form,
  showFirmnessLevel = true,
}: {
  form: UseFormReturn<BidFormValues>;
  showFirmnessLevel?: boolean;
}) => {
  const hasRaffleValue = useWatch({ control: form.control, name: "hasRaffle" });

  return (
    <>
      <FormField
        control={form.control}
        name="deliveryInfo"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              How far does the delivery team need to go? Is the party on the
              same level as where the truck can unload?
            </FormLabel>
            <FormControl>
              <Textarea maxLength={20000} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="interestedGames"
        render={({ field }) => (
          <FormItem>
            <FormLabel>What games are you interested in?</FormLabel>
            <FormDescription className="text-muted-foreground">
              Options are as follows:
              <br />
              Craps can hold up to 16 people (3 dealers).
              <br /> Roulette can hold up to 10 people (1 dealer).
              <br /> Poker "Texas Hold'em" can hold up to 10 people (1 dealer).
              <br /> Blackjack can hold up to 8 people (1 dealer).
              <br /> Ultimate Texas Hold'em can hold up to 8 people (1 dealer).
              <br /> Baccarat can hold up to 8 people (1 dealer).
            </FormDescription>
            <FormControl>
              <Textarea maxLength={20000} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="hasRaffle"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Will you have a raffle after the casino party?
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="YES">Yes</SelectItem>
                <SelectItem value="NO">No</SelectItem>
                <SelectItem value="UNSURE">Unsure</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {hasRaffleValue === "YES" && (
        <FormField
          control={form.control}
          name="wantUsToDoRaffle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Do you want us to do the raffle?</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="YES">Yes</SelectItem>
                  <SelectItem value="NO">No</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="foodInfo"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Will you be eating before or during the casino party?
            </FormLabel>
            <FormControl>
              <Textarea maxLength={20000} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {showFirmnessLevel ? (
        <FormField
          control={form.control}
          name="firmnessLevel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Dealer Firmness (how many raffle tickets would each customer
                receive)
              </FormLabel>
              <FormDescription className="text-muted-foreground">
                Where there is no money involved, we are here to create a great
                atmosphere. When people are losing chips they dont have fun, so
                we have 3 different levels. <br />
                <br /> 1 Firm (play all the games exactly how they are in
                Nevada) <br /> 2 Soft (give the players a some advantage here
                and there)
                <br />3 Loosy-goosy (just have a great time!) <br />
                <br /> Note: Poker has to be played firm because they play
                against each other not the dealer.
              </FormDescription>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select firmness level" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="FIRM">Firm</SelectItem>
                  <SelectItem value="SOFT">Soft</SelectItem>
                  <SelectItem value="LOOSYGOOSY">Loosy-goosy</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </>
  );
};
